import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { IconUserCheck } from "@ui/icons/IconUserCheck"
import { Page } from "@ui/page/Page"
import { Typo } from "@ui/text/Typo"
import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { getFriendlyErrorMessage } from "src/utils/errors"
import { Avatar, DS, DetailRow, HeroButton, Section } from "../ds/Kit"

const REFERRAL_BORDER = "#E8EDF3"
const REFERRAL_SHADOW = "0 1px 2px rgba(15, 23, 42, 0.03), 0 8px 24px rgba(15, 23, 42, 0.04)"
const REFERRAL_MUTED = "#64748B"
const REFERRAL_MOBILE = "@media (max-width: 768px)"

const formatDate = (value: string) => {
	const parsed = new Date(value.replace(" ", "T"))
	return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

export const ReferralPage = () => {
	const [data, setData] = useState<ICompanyReferralResponse | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	const load = useCallback(async () => {
		setLoading(true)
		setError(null)
		const response = await GD.REQ_COMPANY_REFERRAL.request()
		setLoading(false)
		if (response.error) {
			setError(getFriendlyErrorMessage(response.error, "Could not load the referral program"))
			return
		}
		setData(response.data)
	}, [])

	useEffect(() => { void load() }, [load])

	const copyCode = async () => {
		if (!data?.code) return
		try {
			await navigator.clipboard.writeText(data.code)
			toast.success("Referral code copied")
		} catch {
			toast.error("Could not copy the referral code")
		}
	}

	return (
		<Page
			label="Referrals"
			description="Share your company code and track referred companies."
			sx={{ boxShadow: "none", paddingTop: "24px" }}
		>
			<VBox gap="14px">
				{loading ? (
					<Box sx={{ padding: "24px", border: `1px solid ${REFERRAL_BORDER}`, borderRadius: "14px", backgroundColor: "#FFFFFF", boxShadow: REFERRAL_SHADOW }}>
						<Typo sx={{ color: REFERRAL_MUTED }}>Loading referral details…</Typo>
					</Box>
				) : null}
				{error ? (
					<VBox gap="12px" sx={{ padding: "20px", border: "1px solid #FECACA", borderRadius: "14px", backgroundColor: "#FEF2F2", boxShadow: REFERRAL_SHADOW }}>
						<Typo sx={{ color: "#991B1B" }}>{error}</Typo>
						<Button title="Try again" variant="secondarySmall" onClick={() => void load()} sx={{ alignSelf: "flex-start", [REFERRAL_MOBILE]: { minHeight: "44px" } }} />
					</VBox>
				) : null}
				{!loading && !error && data ? (
					<>
						<HBox
							sx={{
								alignItems: "center",
								justifyContent: "space-between",
								gap: "20px",
								flexWrap: "wrap",
								padding: "26px 28px",
								borderRadius: DS.radius,
								background: DS.heroGradient,
								boxShadow: "0 14px 32px rgba(30, 58, 95, 0.28)",
								[REFERRAL_MOBILE]: { padding: "20px" }
							}}
						>
							<VBox gap="8px" sx={{ minWidth: 0 }}>
								<Typo sx={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", fontWeight: 500 }}>Your company code</Typo>
								<Typo sx={{ color: "#FFFFFF", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", lineHeight: 1.05, fontWeight: 800, letterSpacing: ".14em", overflowWrap: "anywhere" }}>{data.code}</Typo>
								<Typo sx={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", lineHeight: 1.45 }}>Share this code before the company submits its registration form.</Typo>
							</VBox>
							<HBox gap="16px" sx={{ alignItems: "center", [REFERRAL_MOBILE]: { width: "100%", justifyContent: "space-between" } }}>
								<VBox gap="2px" sx={{ alignItems: "flex-end", paddingRight: "16px", borderRight: "1px solid rgba(255,255,255,0.25)" }}>
									<Typo sx={{ color: "#FFFFFF", fontSize: "28px", fontWeight: 800, lineHeight: 1 }}>{data.referrals.length}</Typo>
									<Typo sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>Invited companies</Typo>
								</VBox>
								<HeroButton onClick={() => void copyCode()}>Copy code</HeroButton>
							</HBox>
						</HBox>

						<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px", [REFERRAL_MOBILE]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
							{[
								["Share your code", "Send it to a company you work with."],
								["They register", "The company enters the code in its signup form."],
								["Track it here", "Registered companies appear in the list below."]
							].map(([title, text], index) => (
								<HBox key={title} gap="12px" sx={{ alignItems: "flex-start", padding: "16px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.surface, boxShadow: DS.shadow }}>
									<Box sx={{ width: "28px", height: "28px", minWidth: "28px", borderRadius: "9px", background: DS.accentSoft, color: DS.accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "13px" }}>{index + 1}</Box>
									<VBox gap="2px">
										<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 650 }}>{title}</Typo>
										<Typo sx={{ color: DS.muted, fontSize: "13px", lineHeight: 1.45 }}>{text}</Typo>
									</VBox>
								</HBox>
							))}
						</Box>

						{data.referred_by ? (
							<Section>
								<DetailRow
									label="Referred by"
									value={
										<VBox gap="2px" sx={{ alignItems: "flex-end" }}>
											<Typo sx={{ color: DS.ink, fontWeight: 650, fontSize: "14px" }}>{data.referred_by.name}</Typo>
											<Typo sx={{ color: DS.muted, fontSize: "12px" }}>{formatDate(data.referred_by.referred_at)}</Typo>
										</VBox>
									}
								/>
							</Section>
						) : null}

						<VBox gap="0" sx={{ overflow: "hidden", border: `1px solid ${DS.line}`, borderRadius: DS.radius, backgroundColor: DS.surface, boxShadow: DS.shadow }}>
							<HBox sx={{ justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 18px" }}>
								<Typo sx={{ color: DS.ink, fontSize: "15px", fontWeight: 650 }}>Invited companies</Typo>
								<Typo sx={{ color: DS.muted, fontSize: "12px", padding: "2px 10px", borderRadius: "999px", background: DS.soft }}>{data.referrals.length} total</Typo>
							</HBox>
							{data.referrals.length === 0 ? (
								<VBox gap="8px" sx={{ padding: "40px 24px 48px", alignItems: "center", textAlign: "center", borderTop: `1px solid ${DS.line}` }}>
									<Box sx={{ width: "44px", height: "44px", borderRadius: "50%", background: DS.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
										<IconUserCheck size={20} color={DS.accent} />
									</Box>
									<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 650 }}>No referrals yet</Typo>
									<Typo sx={{ color: DS.muted, fontSize: "13px" }}>No companies have registered with this code yet.</Typo>
								</VBox>
							) : (
								<>
									<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(150px, .7fr) minmax(180px, .6fr)", gap: "16px", padding: "10px 18px", backgroundColor: DS.soft, borderTop: `1px solid ${DS.line}`, borderBottom: `1px solid ${DS.line}`, [REFERRAL_MOBILE]: { display: "none" } }}>
										<Typo sx={{ fontSize: "12px", fontWeight: 600, color: DS.muted }}>Company</Typo>
										<Typo sx={{ fontSize: "12px", fontWeight: 600, color: DS.muted }}>Source</Typo>
										<Typo sx={{ fontSize: "12px", fontWeight: 600, color: DS.muted, textAlign: "right" }}>Joined</Typo>
									</Box>
									{data.referrals.map(item => (
										<Box key={item.uid} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(150px, .7fr) minmax(180px, .6fr)", alignItems: "center", gap: "16px", padding: "14px 18px", borderBottom: `1px solid ${DS.line}`, "&:last-child": { borderBottom: "none" }, [REFERRAL_MOBILE]: { gridTemplateColumns: "minmax(0, 1fr)", gap: "5px" } }}>
											<HBox gap="10px" sx={{ alignItems: "center", minWidth: 0 }}>
												<Avatar name={item.name} size={32} />
												<Typo sx={{ minWidth: 0, color: DS.ink, fontWeight: 650, overflowWrap: "anywhere" }}>{item.name}</Typo>
											</HBox>
											<Typo sx={{ color: DS.muted, fontSize: "13px" }}>{item.type} · {item.registration_source}</Typo>
											<Typo sx={{ color: DS.muted, fontSize: "13px", textAlign: "right", [REFERRAL_MOBILE]: { textAlign: "left" } }}>{formatDate(item.referred_at)}</Typo>
										</Box>
									))}
								</>
							)}
						</VBox>
					</>
				) : null}
			</VBox>
		</Page>
	)
}
